import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { AlertIcon } from "@icons";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import {
    CloseTemplateTaskRow,
    CloseTemplateTaskCallbacks,
    CloseTemplateTaskMeta,
} from "../closeTemplateTasksViewConfig";

type Meta = ViewTableMeta<CloseTemplateTaskCallbacks, never> &
    CloseTemplateTaskMeta;

export const CloseTemplateTaskActionsCell = memo(
    function CloseTemplateTaskActionsCell({
        row,
        table,
    }: CellContext<ViewTableFeatures, CloseTemplateTaskRow, string>) {
        const meta = (table.options.meta ?? {}) as Meta;
        const item = row.original;

        if (item.isGroupRow) {
            const isWhitelisted =
                item.closeStageId != null &&
                (meta.whitelistedStageIds?.has(item.closeStageId) ?? false);
            const isEmpty =
                (meta.activeTaskCountByStageKey?.get(item.id) ?? 0) === 0;
            const shouldShowRemove = isWhitelisted && isEmpty;

            if (!shouldShowRemove) {
                return null;
            }

            return (
                <ViewTableCell captureClicks>
                    <HStack align="center" justify="end">
                        <Menu.More className={viewTableStyles.showOnHover}>
                            <Menu.Item
                                onClick={() => {
                                    if (item.closeStageId) {
                                        meta.callbacks?.onRemoveStageFromWhitelist(
                                            item.closeStageId,
                                        );
                                    }
                                }}
                                destructive
                            >
                                Remove
                            </Menu.Item>
                        </Menu.More>
                    </HStack>
                </ViewTableCell>
            );
        }

        const isOutdated = meta.outdatedLocalIds?.has(item.id) ?? false;

        return (
            <ViewTableCell captureClicks>
                <HStack gap={8} align="center" justify="end">
                    {isOutdated && (
                        <Tooltip content="Newer version of the task template available">
                            <span
                                role="img"
                                aria-label="Newer version of the task template available"
                            >
                                <AlertIcon className="icon-color-yellow-600" />
                            </span>
                        </Tooltip>
                    )}
                    <Button
                        variant="primary"
                        size="sm"
                        className={viewTableStyles.showOnHover}
                        onClick={() => meta.callbacks?.onEditTask(item.id)}
                    >
                        Edit
                    </Button>
                    <Menu.More className={viewTableStyles.showOnHover}>
                        <Menu.Item
                            onClick={() =>
                                meta.callbacks?.onRemoveTask(item.id)
                            }
                            destructive
                        >
                            Delete
                        </Menu.Item>
                    </Menu.More>
                </HStack>
            </ViewTableCell>
        );
    },
);
